import { memo, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../api";
import { toast } from "sonner";
import { Scissors, Plus, Check, X, Play, Pause, AlertTriangle, HelpCircle, Loader2, ChevronUp, ChevronDown } from "lucide-react";
import CustomVideoPlayer from "../CustomVideoPlayer";

export default function ScopeReportPanel({ matchId, currentClanId }) {
  const [reports, setReports] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  
  // حالة منع التكرار لزر حفظ ونشر الكارد
  const [isSubmitting, setIsSubmitting] = useState(false);

  // حالات خط التحميل والرفع
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // خريطة ترجمة أنواع المخالفات إلى العربية
  const violationNamesMap = {
    "burn": "حرق",
    "double_zoom": "تدبيل زوم",
    "burn_clickcam": "حرق كل كام",
    "double_zoom_clickcam": "تدبيل كل كام"
  };

  const [form, setForm] = useState({
    map_number: 1, // قيمة افتراضية ترسل تلقائياً دون الحاجة لخانة في الواجهة
    map_name: "",
    round_number: "0-0",
    violation_type: "burn",
    notes: ""
  });

  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const reportsRequestRef = useRef(null);
  const createBusyRef = useRef(false);
  const uploadBusyRef = useRef(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const fetchReports = useCallback(() => {
    if (reportsRequestRef.current) return reportsRequestRef.current.promise;
    const controller = new AbortController();
    const request = { controller, promise: null };
    request.promise = api.get(`/matches/${matchId}/scope-reports`, { signal: controller.signal, timeout: 10000 })
      .then(({ data }) => { if (!controller.signal.aborted) { setReports(data || []); setAccessDenied(false); } })
      .catch(err => {
        if (!controller.signal.aborted && [401, 403, 404].includes(err.response?.status)) {
          setAccessDenied(true);
          setReports([]);
        }
      })
      .finally(() => { if (reportsRequestRef.current === request) reportsRequestRef.current = null; });
    reportsRequestRef.current = request;
    return request.promise;
  }, [matchId]);

  useEffect(() => {
    setReports([]);
    setAccessDenied(false);
    fetchReports();
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchReports();
    }, 5000);
    return () => {
      clearInterval(interval);
      reportsRequestRef.current?.controller.abort();
      reportsRequestRef.current = null;
    };
  }, [fetchReports]);

  useEffect(() => () => {
    if (videoUrl.startsWith("blob:")) URL.revokeObjectURL(videoUrl);
  }, [videoUrl]);

  // إنشاء كارد جديد مع تفعيل حماية منع الضغط المتكرر
  const handleCreateReport = async (e) => {
    e.preventDefault();
    if (createBusyRef.current) return;
    if (!form.map_name.trim()) {
      return toast.error("اسم الماب حقل إلزامي!");
    }
    createBusyRef.current = true;
    try {
      setIsSubmitting(true);
      await api.post(`/matches/${matchId}/scope-reports`, form);
      toast.success("تم إنشاء كارد البلاغ بنجاح!");
      setShowCreateModal(false);
      setForm({
        map_number: 1,
        map_name: "",
        round_number: "0-0",
        violation_type: "burn",
        notes: ""
      });
      fetchReports();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "حدث خطأ");
    } finally {
      createBusyRef.current = false;
      setIsSubmitting(false);
    }
  };

  // دوال التحكم بالأسهم للراوندات (الجانب الأيمن والأيسر بحد أقصى 8)
  const handleRoundLeftIncrement = () => {
    const parts = (form.round_number || "0-0").split("-");
    const left = parseInt(parts[0]) || 0;
    const right = parts[1] || "0";
    if (left < 8) {
      setForm({ ...form, round_number: `${left + 1}-${right}` });
    }
  };

  const handleRoundLeftDecrement = () => {
    const parts = (form.round_number || "0-0").split("-");
    const left = parseInt(parts[0]) || 0;
    const right = parts[1] || "0";
    if (left > 0) {
      setForm({ ...form, round_number: `${left - 1}-${right}` });
    }
  };

  const handleRoundRightIncrement = () => {
    const parts = (form.round_number || "0-0").split("-");
    const left = parts[0] || "0";
    const right = parseInt(parts[1]) || 0;
    if (right < 8) {
      setForm({ ...form, round_number: `${left}-${right + 1}` });
    }
  };

  const handleRoundRightDecrement = () => {
    const parts = (form.round_number || "0-0").split("-");
    const left = parts[0] || "0";
    const right = parseInt(parts[1]) || 0;
    if (right > 0) {
      setForm({ ...form, round_number: `${left}-${right - 1}` });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCurrentTime(0);
      setVideoFile(file);
      setVideoUrl(URL.createObjectURL(file));
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  // رفع المقطع المحدد وضبط التوقيت مع خط التحميل التفاعلي
  const handleUploadDone = async () => {
    if (uploadBusyRef.current) return;
    if ((!videoFile && !videoUrl) || !selectedReportId) return toast.error("الرجاء اختيار مقطع الفيديو");

    uploadBusyRef.current = true;
    const formData = new FormData();
    formData.append("file", videoFile);

    try {
      setIsUploading(true);
      setUploadProgress(10);
      toast.loading("جاري رفع ومعالجة مقطع الفيديو...", { id: "uploading" });

      let videoServerUrl = videoUrl;
      if (videoFile) {
      const uploadRes = await api.post("/upload/video", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 90) / progressEvent.total);
            setUploadProgress(percent);
          }
        }
      });
      
      setUploadProgress(95);
      videoServerUrl = uploadRes.data.url;
      setVideoUrl(videoServerUrl);
      setVideoFile(null);
      }

      await api.put(`/matches/${matchId}/scope-reports/${selectedReportId}`, {
        video_url: videoServerUrl,
        start_zoom_time: currentTime,
        status: "active"
      });

      setUploadProgress(100);
      toast.success("تم رفع المقطع؛ سيظهر في الشات بعد اكتمال المعالجة.", { id: "uploading" });
      
      setShowUploadModal(false);
      setVideoFile(null);
      setVideoUrl("");
      fetchReports();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "تعذّر رفع المقطع", { id: "uploading" });
    } finally {
      uploadBusyRef.current = false;
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="bg-[#111113]/90 border border-white/10 rounded-2xl p-3 sm:p-4 space-y-4 shadow-xl w-full max-w-full overflow-hidden" dir="rtl">
      {/* رأس اللوحة */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-white/10 pb-3 gap-2">
        <h3 className="font-display font-black text-xs sm:text-sm text-white flex items-center gap-1.5 leading-snug">
          <AlertTriangle size={15} className="text-gold-500 shrink-0" /> بلاغات السكوب زوم (الحرق والتدبيل)
        </h3>
        <button
          disabled={accessDenied}
          onClick={() => setShowCreateModal(true)}
          className="w-full sm:w-auto px-3 py-2 rounded-xl bg-gold-500 text-black font-black text-xs hover:bg-gold-400 transition shadow-md text-center shrink-0"
        >
          + إضافة بلاغ جديد
        </button>
      </div>

      <div className="space-y-3 max-h-[450px] overflow-y-auto pr-1">
        {reports.length === 0 ? (
          <div className="text-center py-6 text-xs text-white/40">{accessDenied ? "البلاغات متاحة لأطراف المباراة والمشرفين فقط." : "لا توجد بلاغات سكوب زوم مسجلة حتى الآن."}</div>
        ) : (
          reports.map((report) => (
            <div key={report.id} className="bg-black/40 border border-white/10 rounded-xl p-3 space-y-2.5">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center text-xs gap-1">
                <span className="font-bold text-gold-400">
                  {report.map_name ? `ماب (${report.map_name})` : ""} • راوند {report.round_number}
                </span>
                <span className="px-2 py-0.5 rounded bg-white/5 text-[10px] text-gold-300 font-bold uppercase">
                  {violationNamesMap[report.violation_type] || report.violation_type}
                </span>
              </div>

              <div className="text-[11px] text-white/60">
                المبلغ: <span className="text-white font-bold">{report.reporter_username}</span>
              </div>

              {report.processing_error && <div className="text-xs text-red-400">توقفت المعالجة أو لم تكتمل؛ يمكن لفريق المبلّغ إعادة المحاولة من زر تعديل المقطع.</div>}
              {report.processing ? (
                <div className="text-xs text-white/60">جاري معالجة المقطع…</div>
              ) : !report.video_url && report.can_edit ? (
                <button
                  onClick={() => { 
                    setSelectedReportId(report.id);
                    setVideoFile(null);
                    setVideoUrl(report.source_video_url || "");
                    setCurrentTime(report.start_zoom_time || 0);
                    setShowGuideModal(true);
                  }}
                  className="w-full py-2.5 rounded-xl bg-white/5 border border-dashed border-white/20 hover:bg-white/10 text-xs font-bold text-gold-400 flex items-center justify-center gap-1.5 transition"
                >
                  <Plus size={14} /> إضافة لقطة الفيديو (ثبات)
                </button>
              ) : report.video_url ? (
                <div className="space-y-2">
                  <CustomVideoPlayer src={report.video_url} />
                  {report.can_edit && (
                    <button className="text-xs text-gold-400" onClick={() => {
                      setSelectedReportId(report.id);
                      setVideoFile(null);
                      setVideoUrl(report.source_video_url || "");
                      setCurrentTime(report.start_zoom_time || 0);
                      setShowGuideModal(true);
                    }}>تعديل المقطع</button>
                  )}
                  
                  <div className="text-[10px] text-emerald-400 font-bold flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1">
                    <span>✅ تم إرفاق المقطع للمراجعة</span>
                    <span className="text-white/40">البداية: {report.start_zoom_time}s</span>
                  </div>
                </div>
              ) : (<div className="text-xs text-white/50">بانتظار إرفاق المقطع من كلان المبلّغ.</div>)}
            </div>
          ))
        )}
      </div>

      {/* مودال إنشاء الكارد */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-md p-3 sm:p-4 overflow-y-auto">
          <form onSubmit={handleCreateReport} className="bg-[#141417] border border-white/15 rounded-3xl p-4 sm:p-6 w-full max-w-sm space-y-4 my-auto shadow-2xl">
            <h3 className="font-display font-black text-sm sm:text-base text-white">إضافة بلاغ سكوب زوم جديد</h3>

            {/* خانة الراوندات المزدوجة (يمين ويسار وبينهما -) */}
            <div>
              <label className="text-xs text-white/70 block mb-1">الراوندات (مثال: 8-1)</label>
              <div className="flex items-center gap-2" dir="ltr">
                {/* الجانب الأيمن */}
                <div className="relative flex items-center w-full">
                  <input
                    type="text"
                    maxLength="1"
                    placeholder="0"
                    value={(form.round_number || "0-0").split("-")[0] || "0"}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      const parts = (form.round_number || "0-0").split("-");
                      const right = parts[1] || "0";
                      if (val === "" || (parseInt(val) >= 0 && parseInt(val) <= 8)) {
                        setForm({ ...form, round_number: `${val || "0"}-${right}` });
                      }
                    }}
                    className="w-full bg-black/40 border border-white/10 rounded-xl py-2.5 text-xs text-white text-center outline-none focus:border-gold-500/50 pl-6"
                  />
                  <div className="absolute left-1 flex flex-col justify-center h-full">
                    <button type="button" onClick={handleRoundLeftIncrement} className="text-white/60 hover:text-white p-0.5">
                      <ChevronUp size={12} />
                    </button>
                    <button type="button" onClick={handleRoundLeftDecrement} className="text-white/60 hover:text-white p-0.5">
                      <ChevronDown size={12} />
                    </button>
                  </div>
                </div>

                <span className="text-gold-500 font-bold text-lg px-0.5">-</span>

                {/* الجانب الأيسر */}
                <div className="relative flex items-center w-full">
                  <input
                    type="text"
                    maxLength="1"
                    placeholder="0"
                    value={(form.round_number || "0-0").split("-")[1] || "0"}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "");
                      const parts = (form.round_number || "0-0").split("-");
                      const left = parts[0] || "0";
                      if (val === "" || (parseInt(val) >= 0 && parseInt(val) <= 8)) {
                        setForm({ ...form, round_number: `${left}-${val || "0"}` });
                      }
                    }}
                    className="w-full bg-black/40 border border-white/10 rounded-xl py-2.5 text-xs text-white text-center outline-none focus:border-gold-500/50 pl-6"
                  />
                  <div className="absolute left-1 flex flex-col justify-center h-full">
                    <button type="button" onClick={handleRoundRightIncrement} className="text-white/60 hover:text-white p-0.5">
                      <ChevronUp size={12} />
                    </button>
                    <button type="button" onClick={handleRoundRightDecrement} className="text-white/60 hover:text-white p-0.5">
                      <ChevronDown size={12} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs text-white/70 block mb-1">اسم الماب <span className="text-destructive">*</span></label>
              <input
                type="text"
                required
                placeholder="أدخل اسم الماب (مثال: Raid, Standoff)"
                value={form.map_name}
                onChange={(e) => setForm({ ...form, map_name: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-gold-500/50"
              />
            </div>

            <div>
              <label className="text-xs text-white/70 block mb-1">نوع المخالفة</label>
              <select
                value={form.violation_type}
                onChange={(e) => setForm({ ...form, violation_type: e.target.value })}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-white outline-none focus:border-gold-500/50"
              >
                <option value="burn">حرق (Burn - 0.8s)</option>
                <option value="double_zoom">تدبيل زوم (Double Zoom - 0.3s)</option>
                <option value="burn_clickcam">حرق كل كام</option>
                <option value="double_zoom_clickcam">تدبيل كل كام</option>
              </select>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
              <button 
                type="button" 
                onClick={() => setShowCreateModal(false)} 
                disabled={isSubmitting}
                className="w-full sm:w-auto px-3 py-2.5 text-xs text-white/60 hover:text-white disabled:opacity-50"
              >
                إلغاء
              </button>
              <button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full sm:w-auto px-4 py-2.5 bg-gold-500 text-black font-black text-xs rounded-xl hover:bg-gold-400 transition disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    <span>جاري الحفظ...</span>
                  </>
                ) : (
                  "حفظ ونشر الكارد"
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* مودال دليل طريقة تحديد فتحة السكوب */}
      {showGuideModal && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          <div className="bg-[#141417] border border-white/15 w-full max-w-2xl rounded-3xl p-4 sm:p-6 text-white shadow-2xl space-y-4 my-auto max-h-[90vh] overflow-y-auto">
            <div className="text-center space-y-1">
              <h3 className="text-sm sm:text-base font-black text-gold-400 flex items-center justify-center gap-1.5">
                <HelpCircle size={18} className="shrink-0" /> دليل الطريقة الصحيحة لتحديد فتحة السكوب
              </h3>
              <p className="text-[11px] sm:text-xs text-white/60">يرجى مطابقة لقطتك مع الأمثلة التالية لضمان قبول البلاغ:</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-red-950/20 border border-red-500/30 rounded-2xl p-3 flex flex-col items-center text-center">
                <div className="w-full h-20 sm:h-24 bg-red-900/10 rounded-xl mb-2 flex items-center justify-center border border-red-500/20 text-red-400 font-bold text-xs">
                  ❌ خطأ (مبكر جداً)
                </div>
                <p className="text-xs font-bold text-white/90 mb-0.5">فتح السكوب قبل الهدف بمسافة طويلة</p>
                <p className="text-[10px] sm:text-[11px] text-white/50">يؤدي لرفض البلاغ لعدم دقة التوقيت.</p>
              </div>

              <div className="bg-red-950/20 border border-red-500/30 rounded-2xl p-3 flex flex-col items-center text-center">
                <div className="w-full h-20 sm:h-24 bg-red-900/10 rounded-xl mb-2 flex items-center justify-center border border-red-500/20 text-red-400 font-bold text-xs">
                  ❌ خطأ (متأخر جداً)
                </div>
                <p className="text-xs font-bold text-white/90 mb-0.5">التحديد بعد بدء الطلقة أو الاشتباك</p>
                <p className="text-[10px] sm:text-[11px] text-white/50">يُفقد اللقطة قيمتها الزمنية الحقيقية.</p>
              </div>

              <div className="bg-emerald-950/20 border border-emerald-500/30 rounded-2xl p-3 flex flex-col items-center text-center">
                <div className="w-full h-20 sm:h-24 bg-emerald-900/10 rounded-xl mb-2 flex items-center justify-center border border-emerald-500/20 text-emerald-400 font-bold text-xs">
                  ✔️ صحيحة (بداية الظهور)
                </div>
                <p className="text-xs font-bold text-white/90 mb-0.5">تحديد اللحظة بالضبط عند ظهور السكوب</p>
                <p className="text-[10px] sm:text-[11px] text-white/50">الطريقة المثالية والمقبولة فوراً.</p>
              </div>

              <div className="bg-emerald-950/20 border border-emerald-500/30 rounded-2xl p-3 flex flex-col items-center text-center">
                <div className="w-full h-20 sm:h-24 bg-emerald-900/10 rounded-xl mb-2 flex items-center justify-center border border-emerald-500/20 text-emerald-400 font-bold text-xs">
                  ✔️ صحيحة (الثبات الدقيق)
                </div>
                <p className="text-xs font-bold text-white/90 mb-0.5">التحديد المتزامن مع حركة الخصم</p>
                <p className="text-[10px] sm:text-[11px] text-white/50">يضمن صحة البلاغ وسرعة مراجعته.</p>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
              <button onClick={() => setShowGuideModal(false)} className="w-full sm:w-auto px-4 py-2.5 text-xs text-white/60 hover:text-white">إلغاء</button>
              <button 
                onClick={() => {
                  setShowGuideModal(false);
                  setShowUploadModal(true);
                }} 
                className="w-full sm:w-auto px-6 py-2.5 bg-gold-500 hover:bg-gold-400 text-black font-black text-xs rounded-xl shadow-lg transition"
              >
                فهمت، الانتقال لرفع المقطع ←
              </button>
            </div>
          </div>
        </div>
      )}

      {/* مودال قص ووزن الفيديو مع خط تحميل تفاعلي */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-md p-3 sm:p-4 overflow-y-auto">
          <div className="bg-[#141417] border border-white/15 rounded-3xl p-4 sm:p-6 w-full max-w-md space-y-4 shadow-2xl my-auto">
            <h3 className="font-display font-black text-sm sm:text-base text-white flex items-center gap-1.5">
              <Scissors size={16} className="text-gold-500 shrink-0" /> ضبط وتحديد نقطة فتحة السكوب
            </h3>

            {isUploading ? (
              <div className="py-10 flex flex-col items-center justify-center space-y-4">
                <Loader2 className="w-10 h-10 text-gold-500 animate-spin" />
                <div className="text-center space-y-1 w-full px-2 sm:px-4">
                  <div className="text-xs font-bold text-white">جاري رفع ومعالجة الفيديو ونشره في الشات...</div>
                  <div className="text-[11px] text-gold-400 font-mono font-bold">{uploadProgress}%</div>
                  
                  {/* خط التحميل التفاعلي المتحرك */}
                  <div className="w-full bg-white/10 h-2 rounded-full overflow-hidden mt-3">
                    <div 
                      className="bg-gold-500 h-full transition-all duration-300 rounded-full"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              </div>
            ) : !videoUrl ? (
              <label className="border-2 border-dashed border-white/20 hover:border-gold-500 rounded-2xl h-36 sm:h-40 flex flex-col items-center justify-center cursor-pointer bg-black/30 p-4 text-center transition">
                <span className="text-xs text-white/70 font-bold mb-1">اختر مقطع الفيديو من جهازك</span>
                <input type="file" accept="video/*" onChange={handleFileChange} className="hidden" />
              </label>
            ) : (
              <div className="space-y-3">
                <label className="block text-xs text-gold-400 cursor-pointer">اختيار فيديو بديل<input type="file" accept="video/*" onChange={handleFileChange} className="hidden" /></label>
                <div className="relative rounded-xl overflow-hidden bg-black aspect-video border border-white/10 flex items-center justify-center">
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    muted
                    controls
                    onLoadedMetadata={(e) => setDuration(e.target.duration)}
                    onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
                    className="max-h-full w-auto"
                  />
                  <button onClick={togglePlay} className="absolute bottom-2 left-2 bg-black/70 p-1.5 rounded-lg text-xs text-white">
                    {isPlaying ? <Pause size={12} /> : <Play size={12} />}
                  </button>
                </div>

                <div className="bg-black/40 p-3 rounded-xl border border-white/10 space-y-1">
                  <div className="flex justify-between text-[11px] text-white/60">
                    <span>اسحب لتحديد بداية فتحة السكوب:</span>
                    <span className="text-gold-400 font-bold">{currentTime.toFixed(2)}s</span>
                  </div>
                  <input
                    type="range"
                    dir="ltr"
                    min="0"
                    max={duration || 0}
                    step="0.01"
                    value={currentTime}
                    onChange={(e) => {
                      const t = parseFloat(e.target.value);
                      setCurrentTime(t);
                      if (videoRef.current) videoRef.current.currentTime = t;
                    }}
                    className="w-full accent-gold-500 cursor-pointer h-2 bg-white/10 rounded direction-ltr"
                  />
                </div>
              </div>
            )}

            {!isUploading && (
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
                <button onClick={() => setShowUploadModal(false)} className="w-full sm:w-auto px-3 py-2.5 text-xs text-white/60 hover:text-white">إلغاء</button>
                <button
                  onClick={handleUploadDone}
                  disabled={!videoFile && !videoUrl}
                  className="w-full sm:w-auto px-4 py-2.5 bg-gold-500 text-black font-black text-xs rounded-xl disabled:opacity-50 text-center hover:bg-gold-400 transition"
                >
                  تأكيد ونشر في الشات ✅
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}